import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext.js";
import { api, ApiError } from "../../../api/client.js";
import { OPERATOR_ACTION_ROLE, effectiveImageRole, roleAtLeast } from "../image/image-ui.js";
import type { PersonaSummary, SourceVideoSummary } from "../../../api/types.js";
import { RE_ERROR_TEXT, RE_MAX_SOURCE_SECONDS, readLocalVideoDuration, reSourceTooLong, reSourceTooLongText, type ReProjectDto } from "./re-api.js";
import { ReAutoReButton } from "./ReAuto.js";

/**
 * FIX-011 / RE Stage 4 - step 1 of the RE path: choose a Persona and a source video (a saved upload, a new upload, or the video of a Reel approved in Research - FIX-049 -
 * which arrives as `?srrId=&personaId=`), see it, then start. Starting creates the RE project and sends the video to Grok (stage 3); nothing is produced.
 */
export function ReEntry({ tenantId }: { readonly tenantId: string }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const srrId = params.get("srrId");
  const fromReelPersona = params.get("personaId");
  const { csrfToken, user, memberships } = useAuth();
  const canAct = roleAtLeast(effectiveImageRole({ isPlatformOwner: user?.isPlatformOwner === true, memberships, tenantId }), OPERATOR_ACTION_ROLE);
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [personaId, setPersonaId] = useState<string>(fromReelPersona ?? "");
  const [sources, setSources] = useState<SourceVideoSummary[]>([]);
  const [sourcesTotal, setSourcesTotal] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [sourceVideoId, setSourceVideoId] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const errorText = (e: unknown, fallback: string): string => {
    const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
    return (body?.error !== undefined ? RE_ERROR_TEXT[body.error] : undefined) ?? body?.message ?? fallback;
  };

  useEffect(() => {
    api.get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas`).then((r) => {
      if (!mounted.current) return;
      const usable = r.personas.filter((p) => p.active !== false && p.adminStatus !== "suspended");
      setPersonas(usable);
      if (fromReelPersona === null && usable.length === 1) setPersonaId(usable[0]!.personaId);
    }).catch(() => { if (mounted.current) setError("Could not load your personas."); });
  }, [tenantId, fromReelPersona]);

  async function loadSources(cursor: string | null): Promise<void> {
    try {
      const page = await api.get<{ sourceVideos: SourceVideoSummary[]; totalCount: number; nextCursor: string | null }>(`/videos/${tenantId}/source-videos${cursor !== null ? `?cursor=${encodeURIComponent(cursor)}` : ""}`);
      if (!mounted.current) return;
      setSources((prev) => (cursor === null ? page.sourceVideos : [...prev, ...page.sourceVideos.filter((s) => !prev.some((p) => p.id === s.id))]));
      setSourcesTotal(page.totalCount);
      setNextCursor(page.nextCursor);
    } catch (e) {
      if (mounted.current) setError(errorText(e, "Could not load your previous uploads."));
    }
  }
  useEffect(() => { void loadSources(null); }, [tenantId]);

  async function upload(): Promise<void> {
    if (uploadFile === null) return;
    setError(null);
    const localSeconds = await readLocalVideoDuration(uploadFile);
    if (localSeconds !== null && reSourceTooLong(localSeconds)) {
      setError(reSourceTooLongText(localSeconds)); // refused before anything is uploaded
      return;
    }
    setBusy(true);
    try {
      const response = await fetch(`/videos/${tenantId}/source-videos?contentType=${encodeURIComponent(uploadFile.type || "video/mp4")}`, { method: "POST", credentials: "include", headers: { "x-emma-csrf": csrfToken ?? "" }, body: uploadFile });
      const body = (await response.json()) as SourceVideoSummary & { error?: string };
      if (!response.ok) throw new Error(body.error ?? "upload failed");
      setSources((prev) => (prev.some((s) => s.id === body.id) ? prev : [body, ...prev]));
      setUploadFile(null);
      if (reSourceTooLong(body.durationSeconds)) setError(reSourceTooLongText(body.durationSeconds)); // length could not be read before the upload: the stored video is not selectable for RE
      else setSourceVideoId(body.id);
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : "Upload failed.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function prepareFromReel(): Promise<void> {
    if (srrId === null || personaId === "") return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ sourceVideoId: string }>(`/research/${tenantId}/personas/${encodeURIComponent(personaId)}/srrs/${encodeURIComponent(srrId)}/rp`, csrfToken);
      const record = await api.get<SourceVideoSummary>(`/videos/${tenantId}/source-videos/${encodeURIComponent(r.sourceVideoId)}`);
      if (!mounted.current) return;
      setSources((prev) => (prev.some((s) => s.id === record.id) ? prev : [record, ...prev]));
      if (reSourceTooLong(record.durationSeconds)) setError(`This Reel cannot be used for RE. ${reSourceTooLongText(record.durationSeconds)}`);
      else setSourceVideoId(record.id);
    } catch (e) {
      if (mounted.current) setError(errorText(e, "Could not prepare the source video from this Reel (it may have been removed)."));
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function start(): Promise<void> {
    if (personaId === "" || sourceVideoId === null) return;
    const chosen = sources.find((x) => x.id === sourceVideoId);
    if (chosen !== undefined && reSourceTooLong(chosen.durationSeconds)) { setError(reSourceTooLongText(chosen.durationSeconds)); return; }
    setBusy(true);
    setError(null);
    let created: ReProjectDto | null = null;
    try {
      created = (await api.post<{ project: ReProjectDto }>(`/re/${tenantId}/projects`, csrfToken, { personaId, sourceVideoId, ...(srrId !== null ? { srrId } : {}) })).project;
      await api.post(`/re/${tenantId}/projects/${encodeURIComponent(created.id)}/analysis`, csrfToken, {});
      navigate(`/generate/create/re/projects/${encodeURIComponent(created.id)}`);
    } catch (e) {
      if (!mounted.current) return;
      setError(errorText(e, "Could not start the analysis."));
      // The project exists (saved) even if the request could not be sent: open it so the user can retry from there.
      if (created !== null) navigate(`/generate/create/re/projects/${encodeURIComponent(created.id)}`);
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  const selected = sources.find((s) => s.id === sourceVideoId) ?? null;
  return (
    <section data-testid="re-entry">
      <h3>RE — recreate a video</h3>
      <p className="muted">Pick a persona and a source video. Grok analyzes the video; you review and correct the analysis, then approve it. Nothing is produced in this step. <Link to="/generate/create/re/projects" data-testid="re-my-projects">My RE projects</Link></p>
      {error !== null ? <div className="error-banner" role="alert" data-testid="re-entry-error">{error}</div> : null}
      {srrId !== null ? <p data-testid="re-from-reel" className="card">From an approved Reel. {sourceVideoId === null ? (canAct ? <button type="button" data-testid="re-prepare-from-reel" disabled={busy || personaId === ""} onClick={() => void prepareFromReel()}>Use this Reel's video</button> : null) : "The Reel's video is selected."}</p> : null}

      <label>Persona{" "}
        <select data-testid="re-persona" value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
          <option value="">Select a persona…</option>
          {personas.map((p) => <option key={p.personaId} value={p.personaId}>{p.displayName}</option>)}
        </select>
      </label>

      <h4>Source video</h4>
      <p className="muted" data-testid="re-length-rule">RE accepts source videos of at most {RE_MAX_SOURCE_SECONDS} seconds.</p>
      {selected !== null ? (
        <div className="card" data-testid="re-selected-source">
          <video controls preload="metadata" style={{ maxWidth: 240, maxHeight: 360, background: "#000" }} src={`/videos/${tenantId}/source-videos/${selected.id}/preview`} />
          <p className="muted">{selected.durationSeconds !== null ? `${selected.durationSeconds.toFixed(1)} s · ` : ""}{(selected.sizeBytes / 1024 / 1024).toFixed(1)} MB <button type="button" onClick={() => setSourceVideoId(null)}>Change</button></p>
        </div>
      ) : (
        <>
          {canAct ? (
            <p>
              <input type="file" accept="video/*" data-testid="re-upload-file" onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)} />{" "}
              <button type="button" data-testid="re-upload" disabled={busy || uploadFile === null} onClick={() => void upload()}>Upload</button>
            </p>
          ) : null}
          <p className="muted">{sourcesTotal === null ? "Loading previous uploads…" : sourcesTotal === 0 ? "No previous uploads." : `Or use a previous upload (${sourcesTotal}):`}</p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {sources.map((s) => (
              <button type="button" key={s.id} data-testid="re-source-option" data-source={s.id} data-too-long={reSourceTooLong(s.durationSeconds) ? "true" : "false"} disabled={reSourceTooLong(s.durationSeconds)} title={reSourceTooLong(s.durationSeconds) ? reSourceTooLongText(s.durationSeconds) : undefined} onClick={() => setSourceVideoId(s.id)} style={{ padding: 4, opacity: reSourceTooLong(s.durationSeconds) ? 0.45 : 1 }}>
                <img alt="" width={72} height={128} style={{ objectFit: "cover", display: "block" }} src={`/videos/${tenantId}/source-videos/${s.id}/thumbnail`} />
                <small>{s.durationSeconds !== null ? `${s.durationSeconds.toFixed(1)} s` : "? s"}{reSourceTooLong(s.durationSeconds) ? " · too long" : ""}</small>
              </button>
            ))}
          </div>
          {nextCursor !== null ? <p><button type="button" onClick={() => void loadSources(nextCursor)}>Load more</button></p> : null}
        </>
      )}

      <p>
        {canAct ? (
          <button type="button" data-testid="re-start" disabled={busy || personaId === "" || sourceVideoId === null} onClick={() => void start()}>Start analysis</button>
        ) : <span className="muted">Your role cannot start an RE project.</span>}
      </p>
      {user?.isPlatformOwner === true ? <ReAutoReButton tenantId={tenantId} sourceVideoId={sourceVideoId} csrfToken={csrfToken} /> : null}
    </section>
  );
}
